import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Apple,
  BarChart3,
  BellRing,
  BookHeart,
  BookMarked,
  BookOpen,
  Calendar,
  CalendarCheck,
  Camera,
  CheckSquare,
  ClipboardList,
  CreditCard,
  Cross,
  DollarSign,
  Dumbbell,
  FileText,
  Flame,
  Globe,
  GraduationCap,
  History,
  Home,
  Layers,
  Shapes,
  LayoutDashboard,
  Library,
  ListTodo,
  MessageCircle,
  MessageSquare,
  Microscope,
  NotebookPen,
  PenLine,
  Ruler,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Sun,
  Trophy,
  UserCircle,
  Users,
  Utensils,
  Video,
  Zap,
} from 'lucide-react';

export type StandardPortal = 'coach' | 'coach-my' | 'client' | 'onboarding' | 'public' | 'shared' | 'dev';
export type StandardExposure = 'primary' | 'drawer' | 'contextual' | 'direct' | 'alias' | 'legacy' | 'flow' | 'public' | 'shared-risk' | 'dev';
export type ClientMoreSection = 'Coaching' | 'Training' | 'Nutrition' | 'Faith' | 'Community' | 'Account';
export type ClientPinSection = 'Training' | 'Faith' | 'Community' | 'Record';
export type CoachNavSection = 'Operate' | 'Coach' | 'Grow' | 'Review' | 'System' | 'My Training';
export type CoachPinMode = 'coach' | 'athlete';

export interface StandardRoute {
  path: string;
  title: string;
  portal: StandardPortal;
  exposure: StandardExposure;
  canonicalPath?: string;
  parent?: string;
  icon?: LucideIcon;
  navLabel?: string;
  description?: string;
  preserveDeepLink?: boolean;
  coachEquivalent?: string;
  clientDefaultOrder?: number;
  clientMoreSection?: ClientMoreSection;
  clientMoreOrder?: number;
  clientMoreEntries?: readonly {
    label: string;
    description: string;
    order: number;
  }[];
  clientPinSection?: ClientPinSection;
  clientPinOrder?: number;
  coachNavSection?: CoachNavSection;
  coachNavOrder?: number;
  coachPinMode?: CoachPinMode;
  coachPinOrder?: number;
  coachPinLabel?: string;
  bannerKey?: string;
  bannerLabel?: string;
}

type RouteSeed = readonly [path: string, title: string, exposure: StandardExposure];

function portalRoutes(portal: StandardPortal, seeds: readonly RouteSeed[]): StandardRoute[] {
  return seeds.map(([path, title, exposure]) => ({
    path,
    title,
    portal,
    exposure,
    preserveDeepLink: true,
  }));
}

const coachRoutes = portalRoutes('coach', [
  ['/coach', 'Command', 'primary'],
  ['/coach/review-command', 'Review Command', 'primary'],
  ['/coach/reliability', 'Delivery Command Center', 'primary'],
  ['/coach/standard-memberships', 'Standard Memberships', 'direct'],
  ['/coach/clients', 'Clients', 'primary'],
  ['/coach/clients/audit', 'Client Audit', 'contextual'],
  ['/coach/clients/:clientId/form-check/:formCheckId?', 'Form Check', 'contextual'],
  ['/coach/clients/:id/body', 'Client Body', 'contextual'],
  ['/coach/clients/:id/progress', 'Client Progress', 'contextual'],
  ['/coach/clients/:id/program', 'Client Program', 'contextual'],
  ['/coach/clients/:id/:tab', 'Client Profile', 'contextual'],
  ['/coach/clients/:id', 'Client Profile', 'contextual'],
  ['/coach/members/:userId', 'Member Profile', 'contextual'],
  ['/coach/members', 'Members', 'direct'],
  ['/coach/copilot', 'Copilot', 'primary'],
  ['/coach/forge', 'Forge', 'primary'],
  ['/coach/blueprint', 'Forge', 'alias'],
  ['/coach/schedule', 'Schedule', 'primary'],
  ['/coach/floor', 'Studio floor', 'primary'],
  ['/coach/programs', 'Programs', 'primary'],
  ['/coach/programs/levels', 'Level Studio', 'primary'],
  ['/coach/system', 'The Coach Fe System', 'primary'],
  ['/coach/community', 'Community', 'primary'],
  ['/coach/inbox', 'Inbox', 'primary'],
  ['/coach/communications', 'Communication Scheduler', 'primary'],
  ['/coach/settings/audit', 'Staff Communication Access', 'primary'],
  ['/coach/reminders', 'Reminders', 'primary'],
  ['/coach/settings', 'Settings', 'primary'],
  ['/coach/weekly-review', 'Weekly Review', 'primary'],
  ['/coach/brotherhood-board', 'Community board', 'primary'],
  ['/coach/exercise-library', 'Exercise Library', 'primary'],
  ['/coach/workout-library', 'Workout Library', 'primary'],
  ['/coach/weekly-reports', 'Weekly Reports', 'primary'],
  ['/coach/marketplace', 'Marketplace', 'primary'],
  ['/coach/faith', 'Faith Hub', 'primary'],
  ['/coach/applications', 'Applications', 'primary'],
  ['/coach/accountability', 'Check-ins', 'primary'],
  ['/coach/journal-review', 'Journal Review', 'primary'],
  ['/coach/analytics', 'Analytics', 'primary'],
  ['/coach/payments', 'Payments', 'primary'],
  ['/coach/challenges', 'Challenges', 'primary'],
  ['/coach/challenge-programs/:id', 'Challenge Program', 'contextual'],
  ['/coach/habits', 'Habits', 'primary'],
  ['/coach/playlist-generator', 'Playlists', 'primary'],
  ['/coach/wins', 'Wins', 'direct'],
  ['/coach/vault/new', 'New Vault Resource', 'contextual'],
  ['/coach/vault/:slug/edit', 'Edit Vault Resource', 'contextual'],
  ['/coach/vault', 'Vault', 'primary'],
  ['/coach/courses/:id/edit', 'Edit Course', 'contextual'],
  ['/coach/courses', 'Courses', 'primary'],
]);

const coachMyRoutes = portalRoutes('coach-my', [
  ['/coach/my/today', 'Today', 'primary'],
  ['/coach/my/plan', 'My Plan', 'primary'],
  ['/coach/my/training', 'Training', 'primary'],
  ['/coach/my/courses/:slug/:lessonId', 'Lesson', 'contextual'],
  ['/coach/my/courses/:slug', 'Course', 'contextual'],
  ['/coach/my/courses', 'Courses', 'primary'],
  ['/coach/my/library/vault/:slug', 'Vault Resource', 'contextual'],
  ['/coach/my/library/vault', 'Vault', 'contextual'],
  ['/coach/my/exercise-library', 'Exercise Library', 'direct'],
  ['/coach/my/nutrition', 'Nutrition', 'primary'],
  ['/coach/my/schedule', 'Schedule', 'direct'],
  ['/coach/my/habits', 'Habits', 'primary'],
  ['/coach/my/tasks', 'Tasks', 'direct'],
  ['/coach/my/check-in', 'Check-In', 'primary'],
  ['/coach/my/body', 'Body Command', 'primary'],
  ['/coach/my/progress', 'Progress', 'direct'],
  ['/coach/my/measurements', 'Measurements', 'direct'],
  ['/coach/my/faith/:slug/day/:day', 'Day', 'contextual'],
  ['/coach/my/faith/:slug/finish', 'Finish', 'contextual'],
  ['/coach/my/faith/:slug', 'Faith', 'contextual'],
  ['/coach/my/faith', 'Faith Library', 'primary'],
  ['/coach/my/journal/write', 'Write', 'contextual'],
  ['/coach/my/journal/entries', 'Journal Entries', 'contextual'],
  ['/coach/my/journal/entry/:id', 'Journal Entry', 'contextual'],
  ['/coach/my/journal', 'Journal', 'primary'],
  ['/coach/my/word-study', 'Word Study', 'primary'],
  ['/coach/my/form-check', 'Form Check', 'primary'],
  ['/coach/my/standard-score', 'Standard Score', 'direct'],
  ['/coach/my/history', 'History', 'primary'],
  ['/coach/my/health', 'Data Center', 'contextual'],
  ['/coach/my/recovery', 'Recovery', 'contextual'],
  ['/coach/my/reel', 'Wins', 'alias'],
  ['/coach/my/ledger', 'Notes', 'direct'],
  ['/coach/my/week', 'Your Week', 'direct'],
  ['/coach/my/contract', 'My Standard', 'primary'],
  ['/coach/my/community', 'Community', 'direct'],
  ['/coach/my/challenges/:id', 'Challenge', 'contextual'],
  ['/coach/my/challenges', 'Challenges', 'direct'],
  ['/coach/my/challenge-program/:id', 'Challenge Program', 'contextual'],
  ['/coach/my/members/:userId', 'Member Profile', 'contextual'],
  ['/coach/my/members', 'Community', 'direct'],
  ['/coach/my/billing', 'Billing', 'primary'],
  ['/coach/my/account', 'Account', 'primary'],
  ['/coach/my/film-session/:id', 'Film Session', 'contextual'],
  ['/coach/my/programs', 'Programs', 'direct'],
  ['/coach/my/feed', 'Feed', 'direct'],
  ['/coach/my/divine-mandate', 'Faith Hub', 'alias'],
  ['/coach/my/journal/books', 'My Books', 'contextual'],
  ['/coach/my/bible', 'Bible', 'contextual'],
  ['/coach/my/devotional/reader', 'Devotional', 'contextual'],
  ['/coach/my/devotional/history', 'Devotional History', 'contextual'],
  ['/coach/my/devotional', 'Faith Hub', 'alias'],
  ['/coach/my/coach-playlists', 'Coach Playlists', 'contextual'],
]);

const clientRoutes = portalRoutes('client', [
  ['/client/standard-activation', 'Your Plan', 'flow'],
  ['/client/standard-start', 'Start Here', 'flow'],
  ['/client', 'Today', 'primary'],
  ['/client/plan', 'My Plan', 'primary'],
  ['/client/training', 'Training', 'primary'],
  ['/client/exercise-library', 'Exercise Library', 'direct'],
  ['/client/programs', 'Programs', 'direct'],
  ['/client/library', 'Library', 'direct'],
  ['/client/library/cookbook', 'Cookbook', 'contextual'],
  ['/client/library/vault/:slug', 'Vault Resource', 'contextual'],
  ['/client/library/vault', 'Vault', 'contextual'],
  ['/client/courses/:slug/:lessonId', 'Lesson', 'contextual'],
  ['/client/courses/:slug', 'Course', 'contextual'],
  ['/client/courses', 'Courses', 'direct'],
  ['/client/body', 'Body Command Center', 'direct'],
  ['/client/nutrition', 'Nutrition', 'primary'],
  ['/client/schedule', 'Schedule', 'direct'],
  ['/client/community', 'Community', 'primary'],
  ['/client/comms', 'Coach', 'primary'],
  ['/client/onboarding/complete', 'Getting Started', 'contextual'],
  ['/client/onboarding', 'Getting Started', 'direct'],
  ['/client/progress', 'Progress', 'direct'],
  ['/client/progress-legacy', 'Progress', 'legacy'],
  ['/client/measurements', 'Measurements', 'contextual'],
  ['/client/history', 'History', 'direct'],
  ['/client/health', 'Data Center', 'contextual'],
  ['/client/recovery', 'Recovery', 'contextual'],
  ['/client/habits', 'Habits', 'direct'],
  ['/client/tasks', 'Tasks', 'direct'],
  ['/client/reminders', 'Reminders', 'direct'],
  ['/client/billing', 'Billing', 'direct'],
  ['/client/form-check', 'Form Check', 'direct'],
  ['/client/standard-score', 'Standard Score', 'direct'],
  ['/client/ledger', 'The Ledger', 'direct'],
  ['/client/contract', 'My Standard', 'direct'],
  ['/client/film-session/:id', 'Film Session', 'contextual'],
  ['/client/members/:userId', 'Member Profile', 'contextual'],
  ['/client/members', 'Community', 'direct'],
  ['/client/feed', 'Feed', 'direct'],
  ['/client/week', 'Your Week', 'direct'],
  ['/client/weekly-reports', 'Weekly Reports', 'direct'],
  ['/client/wins', 'Wins', 'direct'],
  ['/client/reel', 'Wins', 'alias'],
  ['/client/divine-mandate', 'Faith Hub', 'alias'],
  ['/client/devotional/reader', 'Devotional', 'contextual'],
  ['/client/devotional/history', 'Devotional History', 'contextual'],
  ['/client/bible/studio', 'Bible Library', 'alias'],
  ['/client/bible', 'Bible', 'contextual'],
  ['/client/reading-plans', 'Faith Hub', 'alias'],
  ['/client/coach-playlists', 'Coach Playlists', 'contextual'],
  ['/client/word-study', 'Word Study', 'contextual'],
  ['/client/faith/:slug/day/:day', 'Day', 'contextual'],
  ['/client/faith/:slug/finish', 'Finish', 'contextual'],
  ['/client/faith/:slug', 'Faith', 'contextual'],
  ['/client/faith', 'Faith Library', 'direct'],
  ['/client/devotional', 'Faith Hub', 'alias'],
  ['/client/journal/books', 'My Books', 'contextual'],
  ['/client/journal/tags', 'Journal Tags', 'contextual'],
  ['/client/journal/write', 'Write', 'contextual'],
  ['/client/journal/entries', 'Entries', 'contextual'],
  ['/client/journal/entry/:id', 'Journal Entry', 'contextual'],
  ['/client/journal', 'Journal', 'direct'],
  ['/client/challenges/:id', 'Challenge', 'contextual'],
  ['/client/challenges', 'Challenges', 'direct'],
  ['/client/challenge-program/:id', 'Challenge Program', 'contextual'],
  ['/client/check-in', 'Weekly Check-In', 'direct'],
  ['/client/referrals', 'Referrals', 'direct'],
  ['/client/account', 'Preferences', 'direct'],
]);

const onboardingRoutes = portalRoutes('onboarding', [
  ['/onboarding', 'Identity', 'alias'],
  ['/onboarding/identity', 'Identity', 'flow'],
  ['/onboarding/assessment/:pillarSlug', 'Assessment', 'flow'],
  ['/onboarding/score', 'Score', 'flow'],
  ['/onboarding/commitment', 'Commitment', 'flow'],
  ['/onboarding/coach', 'Coach', 'flow'],
  ['/onboarding/profile', 'Profile', 'flow'],
  ['/onboarding/signup', 'Signup', 'flow'],
]);

const publicRoutes = portalRoutes('public', [
  ['/', 'The Standard', 'public'],
  ['/login', 'Login', 'public'],
  ['/auth/reset', 'Reset Password', 'public'],
  ['/invite/:token', 'Accept Invite', 'public'],
  ['/groups/join/:slug/:token', 'Join Group', 'public'],
  ['/apply', 'Apply', 'public'],
  ['/waitlist', 'Waitlist', 'public'],
  ['/book-a-call', 'Book a Call', 'public'],
  ['/membership', 'Membership', 'public'],
  ['/marketplace', 'Marketplace', 'public'],
  ['/results', 'Results', 'public'],
  ['/shared-entry/:token', 'Shared Entry', 'public'],
  ['/shared-book/:token', 'Shared Book', 'public'],
  ['/privacy', 'Privacy', 'public'],
  ['/terms', 'Terms', 'public'],
  ['/blog', 'Blog', 'public'],
  ['/blog/:slug', 'Blog Post', 'public'],
]);

const sharedRoutes = portalRoutes('shared', [
  ['/community/groups/:id', 'Group', 'shared-risk'],
  ['/community/groups/:id/settings', 'Group Settings', 'shared-risk'],
]);

const devRoutes = portalRoutes('dev', [
  ['/__dev/components', 'Component Library', 'dev'],
  ['/__dev/faith-library', 'Faith Library Preview', 'dev'],
  ['/__dev/ipad-desktop', 'iPad Desktop Preview', 'dev'],
  ['/__dev/coach-studio-prototype', 'Coach Studio Prototype', 'dev'],
  ['/__dev/contrast-preview', 'Contrast Preview', 'dev'],
  ['/__dev/dashboard-prototype', 'Dashboard Prototype', 'dev'],
  ['/__dev/dm-conversation-prototype', 'DM Conversation Prototype', 'dev'],
  ['/__dev/onboarding-preview', 'Onboarding Preview', 'dev'],
  ['/__dev/membership', 'Membership Preview', 'dev'],
  ['/__dev/nutrition-logging-prototype', 'Nutrition Logging Prototype', 'dev'],
  ['/__dev/program-studio-prototype', 'Program Studio Prototype', 'dev'],
  ['/__dev/social-prototypes', 'Social Prototypes', 'dev'],
  ['/__dev/slim-live', 'Slim Chrome Preview', 'dev'],
]);

const CLIENT_COACH_EQUIVALENTS: Readonly<Record<string, string>> = {
  '/client/standard-activation': '/coach/standard-memberships',
  '/client/standard-start': '/coach/standard-memberships',
  '/client': '/coach/my/today',
  '/client/plan': '/coach/my/plan',
  '/client/training': '/coach/my/training',
  '/client/exercise-library': '/coach/my/exercise-library',
  '/client/programs': '/coach/my/programs',
  '/client/library': '/coach/my/library/vault',
  '/client/library/cookbook': '/coach/my/library/vault',
  '/client/library/vault/:slug': '/coach/my/library/vault/:slug',
  '/client/library/vault': '/coach/my/library/vault',
  '/client/courses/:slug/:lessonId': '/coach/my/courses/:slug/:lessonId',
  '/client/courses/:slug': '/coach/my/courses/:slug',
  '/client/courses': '/coach/my/courses',
  '/client/body': '/coach/my/body',
  '/client/nutrition': '/coach/my/nutrition',
  '/client/schedule': '/coach/my/schedule',
  '/client/community': '/coach/my/community',
  '/client/comms': '/coach/inbox',
  '/client/onboarding/complete': '/coach/my/today',
  '/client/onboarding': '/coach/my/today',
  '/client/progress': '/coach/my/progress',
  '/client/progress-legacy': '/coach/my/progress',
  '/client/measurements': '/coach/my/measurements',
  '/client/history': '/coach/my/history',
  '/client/health': '/coach/my/health',
  '/client/recovery': '/coach/my/recovery',
  '/client/habits': '/coach/my/habits',
  '/client/tasks': '/coach/my/tasks',
  '/client/reminders': '/coach/reminders',
  '/client/billing': '/coach/my/billing',
  '/client/form-check': '/coach/my/form-check',
  '/client/standard-score': '/coach/my/standard-score',
  '/client/ledger': '/coach/my/ledger',
  '/client/week': '/coach/my/week',
  '/client/contract': '/coach/my/contract',
  '/client/film-session/:id': '/coach/my/film-session/:id',
  '/client/members/:userId': '/coach/my/members/:userId',
  '/client/members': '/coach/my/members',
  '/client/feed': '/coach/my/feed',
  '/client/weekly-reports': '/coach/weekly-reports',
  '/client/wins': '/coach/wins',
  '/client/reel': '/coach/wins',
  '/client/divine-mandate': '/coach/my/divine-mandate',
  '/client/devotional/reader': '/coach/my/devotional/reader',
  '/client/devotional/history': '/coach/my/devotional/history',
  '/client/bible/studio': '/coach/my/bible?tab=library',
  '/client/bible': '/coach/my/bible',
  '/client/reading-plans': '/coach/my/faith',
  '/client/coach-playlists': '/coach/my/coach-playlists',
  '/client/word-study': '/coach/my/word-study',
  '/client/faith/:slug/day/:day': '/coach/my/faith/:slug/day/:day',
  '/client/faith/:slug/finish': '/coach/my/faith/:slug/finish',
  '/client/faith/:slug': '/coach/my/faith/:slug',
  '/client/faith': '/coach/my/faith',
  '/client/devotional': '/coach/my/devotional',
  '/client/journal/books': '/coach/my/journal/books',
  '/client/journal/tags': '/coach/my/journal',
  '/client/journal/write': '/coach/my/journal/write',
  '/client/journal/entries': '/coach/my/journal/entries',
  '/client/journal/entry/:id': '/coach/my/journal/entry/:id',
  '/client/journal': '/coach/my/journal',
  '/client/challenges/:id': '/coach/my/challenges/:id',
  '/client/challenges': '/coach/my/challenges',
  '/client/challenge-program/:id': '/coach/my/challenge-program/:id',
  '/client/check-in': '/coach/my/check-in',
  '/client/referrals': '/coach/wins',
  '/client/account': '/coach/my/account',
};

const ROUTE_DETAILS: Readonly<Record<string, Partial<StandardRoute>>> = {
  '/coach': { icon: LayoutDashboard, coachNavSection: 'Operate', coachNavOrder: 0, coachPinMode: 'coach', coachPinOrder: 0, bannerLabel: 'Command' },
  '/coach/communications': { icon: MessageSquare, coachNavSection: 'Operate', coachNavOrder: 2 },
  '/coach/inbox': { icon: MessageSquare, coachNavSection: 'Operate', coachNavOrder: 1, coachPinMode: 'coach', coachPinOrder: 3 },
  '/coach/schedule': { icon: CalendarCheck, coachNavSection: 'Operate', coachNavOrder: 2, coachPinMode: 'coach', coachPinOrder: 4 },
  '/coach/floor': { icon: Dumbbell, coachNavSection: 'Operate', coachNavOrder: 2, navLabel: 'Studio floor', bannerLabel: 'Studio floor' },
  '/coach/accountability': { icon: CheckSquare, coachNavSection: 'Operate', coachNavOrder: 3, coachPinMode: 'coach', coachPinOrder: 8 },
  '/coach/applications': { icon: ClipboardList, coachNavSection: 'Operate', coachNavOrder: 4, coachPinMode: 'coach', coachPinOrder: 3 },
  '/coach/reminders': { icon: BellRing, coachNavSection: 'Operate', coachNavOrder: 5 },

  '/coach/clients': { icon: Users, coachNavSection: 'Coach', coachNavOrder: 0, coachPinMode: 'coach', coachPinOrder: 2 },
  '/coach/forge': { icon: Dumbbell, coachNavSection: 'Coach', coachNavOrder: 1, coachPinMode: 'coach', coachPinOrder: 6 },
  '/coach/programs': { icon: Calendar, coachNavSection: 'Coach', coachNavOrder: 2, coachPinMode: 'coach', coachPinOrder: 7 },
  '/coach/programs/levels': { icon: Layers, coachNavSection: 'Coach', coachNavOrder: 2, navLabel: 'Level Studio', bannerLabel: 'Level Studio' },
  '/coach/system': { icon: Shapes, coachNavSection: 'Coach', coachNavOrder: 2, navLabel: 'Coach Fe System', bannerLabel: 'Coach Fe System' },
  '/coach/vault': { icon: BookMarked, coachNavSection: 'Coach', coachNavOrder: 3, coachPinMode: 'coach', coachPinOrder: 16, bannerLabel: 'Vault' },
  '/coach/courses': { icon: GraduationCap, coachNavSection: 'Coach', coachNavOrder: 4, bannerLabel: 'Courses' },
  '/coach/exercise-library': { icon: BookOpen, coachNavSection: 'Coach', coachNavOrder: 5, coachPinMode: 'coach', coachPinOrder: 10 },
  '/coach/workout-library': { icon: Dumbbell, coachNavSection: 'Coach', coachNavOrder: 6 },

  '/coach/community': { icon: Globe, coachNavSection: 'Grow', coachNavOrder: 0, coachPinMode: 'coach', coachPinOrder: 11 },
  '/coach/challenges': { icon: Trophy, coachNavSection: 'Grow', coachNavOrder: 1, coachPinMode: 'coach', coachPinOrder: 12 },
  '/coach/marketplace': { icon: ShoppingBag, coachNavSection: 'Grow', coachNavOrder: 2, coachPinMode: 'coach', coachPinOrder: 15 },
  '/coach/brotherhood-board': { icon: Trophy, coachNavSection: 'Grow', coachNavOrder: 3 },
  '/coach/playlist-generator': { icon: BookMarked, coachNavSection: 'Grow', coachNavOrder: 4 },

  '/coach/review-command': { icon: ShieldCheck, coachNavSection: 'Review', coachNavOrder: 0, coachPinMode: 'coach', coachPinOrder: 9, bannerLabel: 'Review Command' },
  '/coach/reliability': { icon: Activity, coachNavSection: 'Review', coachNavOrder: 1, coachPinMode: 'coach', coachPinOrder: 20, bannerLabel: 'Delivery Command Center' },
  '/coach/standard-memberships': { icon: ShieldCheck, coachNavSection: 'Review', coachNavOrder: 2, bannerLabel: 'Standard Memberships' },
  '/coach/weekly-review': { icon: CalendarCheck, coachNavSection: 'Review', coachNavOrder: 3 },
  '/coach/journal-review': { icon: NotebookPen, coachNavSection: 'Review', coachNavOrder: 4 },
  '/coach/weekly-reports': { icon: FileText, coachNavSection: 'Review', coachNavOrder: 5, coachPinMode: 'coach', coachPinOrder: 17, coachPinLabel: 'Reports' },
  '/coach/analytics': { icon: BarChart3, coachNavSection: 'Review', coachNavOrder: 6, coachPinMode: 'coach', coachPinOrder: 18 },
  '/coach/payments': { icon: DollarSign, coachNavSection: 'Review', coachNavOrder: 7 },

  '/coach/habits': { icon: CheckSquare, coachNavSection: 'System', coachNavOrder: 0, coachPinMode: 'coach', coachPinOrder: 13 },
  '/coach/faith': { icon: Cross, coachNavSection: 'System', coachNavOrder: 1, coachPinMode: 'coach', coachPinOrder: 14 },
  '/coach/copilot': { icon: Sparkles, coachNavSection: 'System', coachNavOrder: 2, coachPinMode: 'coach', coachPinOrder: 1 },
  '/coach/settings': { icon: Settings, coachNavSection: 'System', coachNavOrder: 3, coachPinMode: 'coach', coachPinOrder: 19 },

  '/coach/my/today': { icon: Sun, coachNavSection: 'My Training', coachNavOrder: 0, coachPinMode: 'athlete', coachPinOrder: 0 },
  '/coach/my/training': { icon: Flame, coachNavSection: 'My Training', coachNavOrder: 1, coachPinMode: 'athlete', coachPinOrder: 1 },
  '/coach/my/courses': { icon: GraduationCap, coachNavSection: 'My Training', coachNavOrder: 2 },
  '/coach/my/nutrition': { icon: Apple, coachNavSection: 'My Training', coachNavOrder: 3, coachPinMode: 'athlete', coachPinOrder: 2 },
  '/coach/my/body': { icon: Activity, coachNavSection: 'My Training', coachNavOrder: 4, navLabel: 'Body Command' },
  '/coach/my/history': { icon: History, coachNavSection: 'My Training', coachNavOrder: 5, coachPinMode: 'athlete', coachPinOrder: 9 },
  '/coach/my/check-in': { icon: ClipboardList, coachNavSection: 'My Training', coachNavOrder: 6, coachPinMode: 'athlete', coachPinOrder: 5 },
  '/coach/my/habits': { icon: CheckSquare, coachNavSection: 'My Training', coachNavOrder: 7, coachPinMode: 'athlete', coachPinOrder: 4 },
  '/coach/my/faith': { icon: BookHeart, coachNavSection: 'My Training', coachNavOrder: 8, coachPinMode: 'athlete', coachPinOrder: 6 },
  '/coach/my/journal': { icon: NotebookPen, coachNavSection: 'My Training', coachNavOrder: 9, coachPinMode: 'athlete', coachPinOrder: 7 },
  '/coach/my/word-study': { icon: Microscope, coachNavSection: 'My Training', coachNavOrder: 10 },
  '/coach/my/form-check': { icon: Video, coachNavSection: 'My Training', coachNavOrder: 11, coachPinMode: 'athlete', coachPinOrder: 8 },
  '/coach/my/contract': { icon: ShieldCheck, coachNavSection: 'My Training', coachNavOrder: 12 },
  '/coach/my/tasks': { icon: ListTodo, coachNavSection: 'My Training', coachNavOrder: 13 },
  '/coach/my/programs': { icon: Dumbbell, coachNavSection: 'My Training', coachNavOrder: 14, navLabel: 'Programs' },
  '/coach/my/billing': { icon: CreditCard, coachNavSection: 'My Training', coachNavOrder: 15 },
  '/coach/my/account': { icon: UserCircle, coachNavSection: 'My Training', coachNavOrder: 16 },
  '/coach/my/progress': { icon: Camera, coachPinMode: 'athlete', coachPinOrder: 3, coachPinLabel: 'Score' },
  '/coach/wins': { icon: Trophy, coachPinMode: 'athlete', coachPinOrder: 10 },
  '/coach/my/ledger': { icon: Zap, coachPinMode: 'athlete', coachPinOrder: 11, coachPinLabel: 'Notes' },

  '/client': { icon: Home, navLabel: 'Today', clientDefaultOrder: 0, clientPinSection: 'Training', clientPinOrder: 0, bannerKey: 'client-today', bannerLabel: 'Today' },
  '/client/plan': { icon: ClipboardList, navLabel: 'My Plan', clientDefaultOrder: 1, clientPinSection: 'Training', clientPinOrder: 1, description: 'Schedule, training, nutrition, and daily standards' },
  '/client/training': { icon: Dumbbell, clientPinSection: 'Training', clientPinOrder: 2, bannerKey: 'client-training', bannerLabel: 'Training' },
  '/client/nutrition': {
    icon: Utensils,
    clientMoreSection: 'Nutrition',
    clientMoreOrder: 0,
    clientMoreEntries: [
      { label: 'Food Log', description: 'Meals, macros, water, scanner', order: 0 },
      { label: 'Meal Plan', description: 'Plan, grocery list, and targets', order: 1 },
    ],
    clientPinSection: 'Training',
    clientPinOrder: 3,
    bannerKey: 'client-nutrition',
    bannerLabel: 'Nutrition',
  },
  '/client/comms': { icon: MessageCircle, clientDefaultOrder: 3, clientMoreSection: 'Coaching', clientMoreOrder: 0, clientPinSection: 'Training', clientPinOrder: 4, navLabel: 'Coach', description: 'DM Coach Fe' },
  '/client/community': { icon: Users, clientPinSection: 'Community', clientPinOrder: 0, bannerLabel: 'Community' },

  '/client/check-in': { icon: ClipboardList, clientMoreSection: 'Coaching', clientMoreOrder: 1, clientPinSection: 'Community', clientPinOrder: 2, navLabel: 'Check-In', description: 'Weekly review and accountability' },
  '/client/form-check': { icon: Video, clientMoreSection: 'Coaching', clientMoreOrder: 2, clientPinSection: 'Community', clientPinOrder: 3, description: 'Send movement videos' },
  '/client/schedule': { icon: Calendar, clientMoreSection: 'Coaching', clientMoreOrder: 3, clientPinSection: 'Training', clientPinOrder: 5, description: 'Book sessions or set workout time' },

  '/client/programs': { icon: Dumbbell, navLabel: 'Programs', clientDefaultOrder: 2, clientMoreSection: 'Training', clientMoreOrder: 0, clientPinSection: 'Training', clientPinOrder: 2, description: 'Assigned plans and eligible catalog' },
  '/client/exercise-library': { icon: BookOpen, clientMoreSection: 'Training', clientMoreOrder: 1, clientPinSection: 'Training', clientPinOrder: 11, navLabel: 'Exercises', description: 'Demos and coaching cues', bannerKey: 'client-exercise-library', bannerLabel: 'Exercise Library' },
  '/client/history': { icon: History, clientMoreSection: 'Training', clientMoreOrder: 2, clientPinSection: 'Training', clientPinOrder: 7, description: 'Past workouts and activity', bannerKey: 'client-history', bannerLabel: 'History' },
  '/client/body': { icon: Activity, clientMoreSection: 'Training', clientMoreOrder: 3, clientPinSection: 'Training', clientPinOrder: 4, navLabel: 'Body', description: 'Score, recovery, measurements, photos, and wearable data', bannerKey: 'client-body', bannerLabel: 'Body Command Center' },

  '/client/faith': { icon: BookHeart, clientMoreSection: 'Faith', clientMoreOrder: 0, description: 'Daily word, Bible, and study', bannerKey: 'client-faith', bannerLabel: 'Faith Library' },
  '/client/journal': { icon: PenLine, clientMoreSection: 'Faith', clientMoreOrder: 1, clientPinSection: 'Faith', clientPinOrder: 0, description: 'Write it down', bannerKey: 'client-journal', bannerLabel: 'Journal' },
  '/client/courses': { icon: GraduationCap, clientMoreSection: 'Faith', clientMoreOrder: 2, description: 'Finish the lesson you already started', bannerKey: 'coach-courses', bannerLabel: 'Courses' },
  '/client/word-study': { icon: BookHeart, clientMoreSection: 'Faith', clientMoreOrder: 3, clientPinSection: 'Faith', clientPinOrder: 2, description: 'Go deeper into scripture' },
  '/client/divine-mandate': { icon: Flame, clientPinSection: 'Faith', clientPinOrder: 1, navLabel: "Today's Directive" },

  '/client/members': { icon: Users, clientMoreSection: 'Community', clientMoreOrder: 0, clientPinSection: 'Record', clientPinOrder: 1, description: 'Members and profiles', bannerKey: 'client-brotherhood', bannerLabel: 'Community' },
  '/client/wins': { icon: Trophy, clientMoreSection: 'Community', clientMoreOrder: 1, clientPinSection: 'Record', clientPinOrder: 0, description: 'PRs, milestones, and proof' },
  '/client/challenges': { icon: Trophy, clientMoreSection: 'Community', clientMoreOrder: 2, clientPinSection: 'Community', clientPinOrder: 1, description: 'Compete and stay sharp' },

  '/client/habits': { icon: CheckSquare, clientMoreSection: 'Account', clientMoreOrder: 0, clientPinSection: 'Training', clientPinOrder: 6, description: 'Daily standards and streaks', bannerKey: 'client-habits', bannerLabel: 'Habits' },
  '/client/tasks': { icon: ListTodo, clientMoreSection: 'Account', clientMoreOrder: 1, clientPinSection: 'Training', clientPinOrder: 8, description: 'Coach-assigned action items' },
  '/client/reminders': { icon: BellRing, clientMoreSection: 'Account', clientMoreOrder: 2, description: 'Follow-ups and nudges' },
  '/client/billing': { icon: CreditCard, clientMoreSection: 'Account', clientMoreOrder: 3, clientPinSection: 'Record', clientPinOrder: 3, description: 'Plan and payment settings' },
  '/client/account': { icon: UserCircle, clientMoreSection: 'Account', clientMoreOrder: 4, clientPinSection: 'Record', clientPinOrder: 4, description: 'Profile, Apple Health, preferences, and notifications' },

  '/client/library': { icon: Library, navLabel: 'Library', description: 'Programs, workouts, and The Vault', clientMoreSection: 'Training', clientMoreOrder: 4, clientPinSection: 'Training', clientPinOrder: 9 },
  '/client/library/vault': { icon: BookMarked, navLabel: 'The Vault', description: 'Guides, programs, and downloads', clientMoreSection: 'Training', clientMoreOrder: 5, clientPinSection: 'Training', clientPinOrder: 10, bannerKey: 'coach-vault', bannerLabel: 'The Vault' },
  '/client/standard-score': { icon: Activity, clientPinSection: 'Community', clientPinOrder: 4, navLabel: 'Score', bannerKey: 'client-score', bannerLabel: 'Score' },
  '/client/measurements': { icon: Ruler, clientPinSection: 'Community', clientPinOrder: 5, navLabel: 'Measure' },
  '/client/contract': { icon: ShieldCheck, clientPinSection: 'Community', clientPinOrder: 6 },
  '/client/journal/entries': { icon: BookOpen, clientPinSection: 'Record', clientPinOrder: 2, navLabel: 'Entries' },
};

const seededRoutes = [
  ...coachRoutes,
  ...coachMyRoutes,
  ...clientRoutes,
  ...onboardingRoutes,
  ...publicRoutes,
  ...sharedRoutes,
  ...devRoutes,
];

export const STANDARD_ROUTES: readonly StandardRoute[] = seededRoutes.map((route) => ({
  ...route,
  ...(ROUTE_DETAILS[route.path] ?? {}),
  coachEquivalent: route.portal === 'client' ? CLIENT_COACH_EQUIVALENTS[route.path] : route.coachEquivalent,
}));

export const CLIENT_ROUTES = STANDARD_ROUTES.filter((route) => route.portal === 'client');
export const COACH_ROUTES = STANDARD_ROUTES.filter((route) => route.portal === 'coach');
export const COACH_MY_ROUTES = STANDARD_ROUTES.filter((route) => route.portal === 'coach-my');

const CLIENT_MORE_ORDER: readonly ClientMoreSection[] = ['Coaching', 'Training', 'Nutrition', 'Faith', 'Community', 'Account'];
const COACH_NAV_ORDER: readonly CoachNavSection[] = ['Operate', 'Coach', 'Grow', 'Review', 'System', 'My Training'];

function withIcon(route: StandardRoute): route is StandardRoute & { icon: LucideIcon } {
  return route.icon != null;
}

export const CLIENT_DEFAULT_TABS = CLIENT_ROUTES
  .filter((route) => route.clientDefaultOrder != null && withIcon(route))
  .sort((a, b) => (a.clientDefaultOrder ?? 0) - (b.clientDefaultOrder ?? 0));

export const CLIENT_MORE_SECTIONS = CLIENT_MORE_ORDER.map((label) => ({
  label,
  items: CLIENT_ROUTES
    .filter((route) => route.clientMoreSection === label && withIcon(route))
    .flatMap((route) => {
      const entries = route.clientMoreEntries;
      if (!entries?.length) {
        return [{
          ...route,
          menuLabel: route.navLabel ?? route.title,
          menuDescription: route.description ?? '',
          menuOrder: route.clientMoreOrder ?? 0,
        }];
      }
      return entries.map((entry) => ({
        ...route,
        menuLabel: entry.label,
        menuDescription: entry.description,
        menuOrder: entry.order,
      }));
    })
    .sort((a, b) => a.menuOrder - b.menuOrder),
}));

export const CLIENT_PINNABLE_ROUTES = CLIENT_ROUTES
  .filter((route) => route.clientPinSection != null && withIcon(route))
  .sort((a, b) => (a.clientPinOrder ?? 0) - (b.clientPinOrder ?? 0));

export const CLIENT_BANNER_ROUTES = CLIENT_ROUTES.filter((route) => route.bannerKey != null);

export const COACH_NAV_GROUPS = COACH_NAV_ORDER.map((label) => ({
  label,
  items: STANDARD_ROUTES
    .filter((route) => route.coachNavSection === label && withIcon(route))
    .sort((a, b) => (a.coachNavOrder ?? 0) - (b.coachNavOrder ?? 0)),
}));

export const COACH_PINNABLE_ROUTES = STANDARD_ROUTES
  .filter((route) => route.coachPinMode != null && withIcon(route))
  .sort((a, b) => (a.coachPinOrder ?? 0) - (b.coachPinOrder ?? 0));

export const DEFAULT_COACH_TABS = ['/coach', '/coach/clients', '/coach/forge', '/coach/inbox'] as const;
export const DEFAULT_COACH_ATHLETE_TABS = ['/coach/my/today', '/coach/my/training', '/coach/my/nutrition', '/coach/my/progress'] as const;

function cleanPath(value: string): string {
  const [pathname] = value.split(/[?#]/, 1);
  if (!pathname) return '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function splitSuffix(value: string): { pathname: string; suffix: string } {
  const match = value.match(/^([^?#]*)(.*)$/);
  return { pathname: cleanPath(match?.[1] || '/'), suffix: match?.[2] || '' };
}

function matchRoutePattern(pattern: string, pathname: string): Record<string, string> | null {
  const patternSegments = cleanPath(pattern).split('/').filter(Boolean);
  const pathSegments = cleanPath(pathname).split('/').filter(Boolean);
  const params: Record<string, string> = {};
  let pathIndex = 0;

  for (const segment of patternSegments) {
    if (segment.startsWith(':')) {
      const optional = segment.endsWith('?');
      const key = segment.slice(1, optional ? -1 : undefined);
      const value = pathSegments[pathIndex];
      if (value == null) {
        if (optional) continue;
        return null;
      }
      params[key] = value;
      pathIndex += 1;
      continue;
    }

    if (pathSegments[pathIndex] !== segment) return null;
    pathIndex += 1;
  }

  return pathIndex === pathSegments.length ? params : null;
}

function routeSpecificity(route: StandardRoute): number {
  const segments = route.path.split('/').filter(Boolean);
  return segments.reduce((score, segment) => score + (segment.startsWith(':') ? 1 : 10), segments.length);
}

const ROUTE_MATCH_ORDER = [...STANDARD_ROUTES].sort((a, b) => routeSpecificity(b) - routeSpecificity(a));

export function resolveStandardRoute(location: string): StandardRoute | undefined {
  const pathname = cleanPath(location);
  return ROUTE_MATCH_ORDER.find((route) => matchRoutePattern(route.path, pathname) != null);
}

export function getStandardRouteTitle(location: string): string {
  return resolveStandardRoute(location)?.title ?? '';
}

function fillRoutePattern(pattern: string, params: Record<string, string>): string {
  return pattern.replace(/:([A-Za-z0-9_]+)\??/g, (_match, key: string) => params[key] ?? '');
}

function mergeTargetSuffix(target: string, sourceSuffix: string): string {
  if (!sourceSuffix) return target;
  if (!target.includes('?') || !sourceSuffix.startsWith('?')) return `${target}${sourceSuffix}`;
  return `${target}&${sourceSuffix.slice(1)}`;
}

export function mapClientLocationToCoach(location: string): string {
  const { pathname, suffix } = splitSuffix(location);
  const route = CLIENT_ROUTES.find((candidate) => matchRoutePattern(candidate.path, pathname) != null);
  if (!route?.coachEquivalent) return '/coach/my/today';
  const params = matchRoutePattern(route.path, pathname) ?? {};
  const target = fillRoutePattern(route.coachEquivalent, params);
  return mergeTargetSuffix(target, suffix);
}

/** Groups tab of the Community page for the current portal. Shared group pages
 * (/community/groups/:id) live outside both portals, so their back action needs
 * this instead of a bare "/community", which has no route. Coaches return to the
 * workspace they were in, using the same stored mode CoachLayout reads. */
export function communityGroupsRoute(isCoach: boolean): string {
  if (!isCoach) return '/client/community?view=groups';
  let mode: string | null = null;
  try { mode = localStorage.getItem('standard-app-mode'); } catch { /* private mode */ }
  return mode === 'athlete' ? '/coach/my/community?view=groups' : '/coach/community?view=groups';
}

export function isSharedCommunityLocation(location: string): boolean {
  const pathname = cleanPath(location);
  return matchRoutePattern('/community/groups/:id', pathname) != null
    || matchRoutePattern('/community/groups/:id/settings', pathname) != null;
}
